var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Checkbox as MuiCheckbox, FormControl as MuiFormControl, FormControlLabel as MuiFormControlLabel, FormHelperText as MuiFormHelperText, styled } from '@mui/material';
import React, { memo, useId, useMemo } from 'react';
import { ThemeUtils, classNames as cn, getAttributes } from '../../../utils';
import { Container, ContainerDisplays, ContainerFlexAlignItems, ContainerFlexGaps, ContainerPaddings } from '../../templates/container';
import { Text, TextHorizontalAlignments, TextHtmlTags, TextVariants } from '../text';
import { CheckboxDesignStyles } from './types';
var FilterColorIcon = styled(Container, {
    name: 'FilterColorIcon',
    overridesResolver: function (wrapperProps, styles) { return [styles.root]; }
})({});
var variantOptionTextConfig = {
    isHtml: true,
    component: TextHtmlTags.Span,
    variant: TextVariants.Body2,
    horizontalAlignment: TextHorizontalAlignments.Center
};
var TextInsideCheckbox = memo(function (_a) {
    var text = _a.text, color = _a.color;
    var containerPadding = ThemeUtils.getResponsiveProp([
        { x: ContainerPaddings.Size_4 }
    ]);
    return (React.createElement(Container, { display: ContainerDisplays.Flex, flexAlignItems: ContainerFlexAlignItems.Center, flexGap: ContainerFlexGaps.Size_2, padding: containerPadding },
        color && React.createElement(FilterColorIcon, { background: color }),
        React.createElement(Text, __assign({}, variantOptionTextConfig, { content: text }))));
});
var Checkbox = function (_a) {
    var props = __rest(_a, []);
    var className = props.className, idProp = props.id, label = props.label, ariaLabel = props["aria-label"], name = props.name, text = props.text, value = props.value, checked = props.checked, disabled = props.disabled, error = props.error, errorLabel = props.errorLabel, onChange = props.onChange, onMouseOver = props.onMouseOver, onMouseLeave = props.onMouseLeave, onFocus = props.onFocus, onBlur = props.onBlur, color = props.color, _b = props.designStyle, designStyle = _b === void 0 ? CheckboxDesignStyles.Standard : _b, icon = props.icon, checkedIcon = props.checkedIcon, rest = __rest(props, ["className", "id", "label", 'aria-label', "name", "text", "value", "checked", "disabled", "error", "errorLabel", "onChange", "onMouseOver", "onMouseLeave", "onFocus", "onBlur", "color", "designStyle", "icon", "checkedIcon"]);
    var defaultId = useId();
    var id = idProp || defaultId;
    var insideCheckbox = useMemo(function () {
        return designStyle === CheckboxDesignStyles.Filter ? (React.createElement(TextInsideCheckbox, { text: text, color: color })) : undefined;
    }, [color, designStyle, text]);
    var checkboxConfig = {
        id: id,
        disableRipple: true,
        disabled: disabled,
        checked: checked,
        value: value,
        icon: insideCheckbox,
        checkedIcon: insideCheckbox,
        onChange: onChange,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave,
        onFocus: onFocus,
        name: name,
        onBlur: onBlur,
        sx: {
            height: '20px',
            width: '20px'
        },
        className: cn(className, designStyle && "".concat(designStyle.toLowerCase()), disabled && 'disabled', error && 'error'),
        inputProps: __assign({}, getAttributes(rest))
    };
    var formLabelConfig = __assign(__assign({ htmlFor: id, label: designStyle === CheckboxDesignStyles.Standard ? label : undefined, 'aria-label': ariaLabel, value: value, checked: checked, disabled: disabled, className: 'checkbox' }, (disabled && {
        'aria-hidden': true
    })), { disableTypography: true });
    var formControlConfig = {
        disabled: disabled,
        error: error
    };
    var helperTextConfig = {
        content: errorLabel,
        variant: TextVariants.Body3,
        component: TextHtmlTags.Span
    };
    var errorHint = errorLabel ? React.createElement(Text, __assign({}, helperTextConfig)) : null;
    return (React.createElement(MuiFormControl, __assign({}, formControlConfig),
        React.createElement(MuiFormControlLabel, __assign({ control: React.createElement(MuiCheckbox, __assign({}, checkboxConfig)) }, formLabelConfig)),
        error && errorHint && React.createElement(MuiFormHelperText, null, errorHint)));
};
export { Checkbox };
